import { generateRandomString } from 'src/lib/crypto';
import { getEnvironment } from 'src/lib/featureFlags';
import { getQueryParams } from 'src/lib/history';
import { sessionStorage } from 'src/lib/localStorage';
import { generateCodePackagePromise } from 'src/lib/pkce';

const OAUTH_REDIRECT_URI = `${window.location.protocol}//${window.location.host}/integration-jira-ui/pagerduty/oauth/callback`;


const postData = (url) => fetch(url, { method: 'POST' }).then(response => response.json());

const oauthCallbackWindow = () => window.location.pathname === '/integration-jira-ui/pagerduty/oauth/callback';
const authorizationFlow = () => {
  const { error, error_description, code } = getQueryParams();
  return Boolean(error || error_description || code);
};

const encodeState = (secret, redirectLocation) => window.btoa(`${secret}|${redirectLocation}`);
const decodeState = (encodedState) => window.atob(encodedState).split('|');

const mergeSearchParams = (source, target) => {
  const sourceUrl = new URL(source);
  const targetUrl = new URL(target);

  // eslint-disable-next-line no-unused-vars
  for (let [key, value] of sourceUrl.searchParams.entries()) {
    targetUrl.searchParams.set(key, value);
  }

  return targetUrl;
};

const cleanup = () => {
  sessionStorage.setItem('integration_jira_silent_oauth_code_verifier', undefined);
  sessionStorage.setItem('integration_jira_silent_oauth_state', undefined);

  const currentUrl = new URL(window.location.href);

  currentUrl.searchParams.delete('code');
  currentUrl.searchParams.delete('error');
  currentUrl.searchParams.delete('error_description');
  currentUrl.searchParams.delete('state');

  // To remove all params from URI
  window.history.replaceState(null, null, currentUrl.pathname + currentUrl.search);
};

const redirectBackWithAuthorizationCredentials = () => {
  const state = sessionStorage.getItem('integration_jira_silent_oauth_state');
  const [, redirectLocation] = decodeState(state);

  const url = mergeSearchParams(window.location.href, redirectLocation);

  window.location.href = url;
};

const buildSessionData = (accessToken, accountDomain) => {
  const {
    appBaseUrl,
    apiBaseUrl,
    accountBaseUrlTemplate,
  } = getEnvironment();

  const accountBaseUrl = accountBaseUrlTemplate.replace('{domain}', accountDomain);

  return {
    accessToken,
    accountDomain,
    environment: {
      apiBaseUrl,
      appBaseUrl,
      accountBaseUrl,
    },
  };
};

const stateMatch = () => {
  const { state } = getQueryParams();
  return state === sessionStorage.getItem('integration_jira_silent_oauth_state');
};

const authorizationCredentialsFromUrl = () => {
  const {
    code: authorizationCode,
    subdomain: accountDomain,
  } = getQueryParams();

  if (authorizationCode && accountDomain) {
    return { authorizationCode, accountDomain };
  }
  return null;
};

const errorMessage = (error) => error || 'Server did not return any login results';

const errorFromUrl = () => {
  let { error_description, error } = getQueryParams();
  return errorMessage(error_description || error);
};

const requestAccessToken = async (authorizationCode, accountDomain) => {
  const codeVerifier = sessionStorage.getItem('integration_jira_silent_oauth_code_verifier');

  const { accountBaseUrlTemplate, oauthClientId } = getEnvironment();
  const accountBaseUrl = accountBaseUrlTemplate.replace('{domain}', accountDomain);

  const tokenUrl = `${accountBaseUrl}/oauth/token?`
                  + `grant_type=authorization_code&`
                  + `client_id=${oauthClientId}&`
                  + `redirect_uri=${OAUTH_REDIRECT_URI}&`
                  + `code=${authorizationCode}&`
                  + `code_verifier=${codeVerifier}`;

  try {
    let { access_token: accessToken, error, error_description } = await postData(tokenUrl);

    if (accessToken) {
      return { accessToken };
    } else {
      return { error: errorMessage(error_description || error) };
    }
  } catch (error) {
    return { error };
  }
};

const initiateAuthorization = async () => {
  const { codeVerifier, codeChallenge, codeChallengeMethod } = await generateCodePackagePromise();
  sessionStorage.setItem('integration_jira_silent_oauth_code_verifier', codeVerifier);

  const secret = generateRandomString(40);
  const redirectLocation = window.location.href;
  const state = encodeState(secret, redirectLocation);
  sessionStorage.setItem('integration_jira_silent_oauth_state', state);

  const { accountBaseUrlTemplate, oauthClientId } = getEnvironment();
  const { subdomain: accountDomain } = getQueryParams();
  const accountBaseUrl = accountBaseUrlTemplate.replace('{domain}', accountDomain);

  const authorizeUrl = `${accountBaseUrl}/oauth/authorize?`
                      + `client_id=${oauthClientId}&`
                      + `redirect_uri=${OAUTH_REDIRECT_URI}&`
                      + `response_type=code&`
                      + `state=${state}&`
                      + `code_challenge_method=${codeChallengeMethod}&`
                      + `code_challenge=${codeChallenge}`;

  // eslint-disable-next-line require-atomic-updates
  window.location.href = authorizeUrl;
};

export const processSilentOAuth = function () {
  return new Promise(async (resolve, reject) => {
    if (oauthCallbackWindow()) {
      redirectBackWithAuthorizationCredentials();
    } else {
      if (authorizationFlow()) {
        if (authorizationCredentialsFromUrl()) {
          if (stateMatch()) {
            const { authorizationCode, accountDomain } = authorizationCredentialsFromUrl();
            const { accessToken, error } = await requestAccessToken(authorizationCode, accountDomain);

            if (accessToken) {
              const sessionData = buildSessionData(accessToken, accountDomain);
              resolve(sessionData);
            } else {
              reject(error);
            }
          } else {
            reject('The state doesn\'t match.');
          }
        } else {
          reject(errorFromUrl());
        }
      } else {
        initiateAuthorization();
        return;
      }

      cleanup();
    }
  });
};
